/**
 * Top-level route map. Mirrors Flutter `app/lib/routes/app_routes.dart` —
 * same patterns so deep-links (`/shipments/:id`, `/containers/browse`, …)
 * work identically across web + mobile.
 *
 * Phase 0 shipped auth + mode-selector + home shell.
 * Phase 1 adds the sender flow (dashboard / list / details / create) +
 * public tracking. Subsequent phases lazy-load each remaining feature
 * route (trips, tracking-live, containers, companies, wallet, …).
 */
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import LoginPage from '@/pages/auth/LoginPage';
import OtpPage from '@/pages/auth/OtpPage';
import ModeSelectorPage from '@/pages/auth/ModeSelectorPage';
import HomeRouter from '@/pages/home/HomeRouter';
import MyShipmentsPage from '@/pages/shipments/MyShipmentsPage';
import ShipmentDetailsPage from '@/pages/shipments/ShipmentDetailsPage';
import CreateShipmentPage from '@/pages/shipments/CreateShipmentPage';
import PublicTrackPage from '@/pages/tracking/PublicTrackPage';
import MyTripsPage from '@/pages/trips/MyTripsPage';
import CreateTripPage from '@/pages/trips/CreateTripPage';
import DriverShipmentsPage from '@/pages/driver/DriverShipmentsPage';
import ShortcodeLookupPage from '@/pages/driver/ShortcodeLookupPage';
import ConfirmPickupPage from '@/pages/driver/ConfirmPickupPage';
import ConfirmDeliveryPage from '@/pages/driver/ConfirmDeliveryPage';
import MyVehiclesPage from '@/pages/vehicles/MyVehiclesPage';
import AddVehiclePage from '@/pages/vehicles/AddVehiclePage';
import EarningsPage from '@/pages/earnings/EarningsPage';
import CompanyRegisterPage from '@/pages/companies/CompanyRegisterPage';
import CompanyEditPage from '@/pages/companies/CompanyEditPage';
import CompanyMembersPage from '@/pages/companies/CompanyMembersPage';
import CompanySubModePage from '@/pages/companies/CompanySubModePage';
import CompanyPricingPresetsPage from '@/pages/companies/CompanyPricingPresetsPage';
import CompanyReviewsPage from '@/pages/companies/CompanyReviewsPage';
import CompanyCustomersPage from '@/pages/companies/CompanyCustomersPage';
import CompanyCustomerDetailPage from '@/pages/companies/CompanyCustomerDetailPage';
import CompanyAccountPage from '@/pages/companies/CompanyAccountPage';
import CompanyKycDocsPage from '@/pages/companies/CompanyKycDocsPage';
import FuelRefillsPage from '@/pages/companies/FuelRefillsPage';
import TripPlanMapPage from '@/pages/companies/TripPlanMapPage';
import CreateForCustomerPage from '@/pages/centers/CreateForCustomerPage';
import DelegatedShipmentsPage from '@/pages/centers/DelegatedShipmentsPage';
import MyContainersPage from '@/pages/containers/MyContainersPage';
import CreateContainerPage from '@/pages/containers/CreateContainerPage';
import ContainerDetailPage from '@/pages/containers/ContainerDetailPage';
import BrowseContainersPage from '@/pages/containers/BrowseContainersPage';
import ContainerJoinPage from '@/pages/containers/ContainerJoinPage';
import ParticipantDetailPage from '@/pages/containers/ParticipantDetailPage';
import OriginWarehousesPage from '@/pages/containers/OriginWarehousesPage';
import WarehouseInboundPage from '@/pages/warehouse/WarehouseInboundPage';

const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/home" replace /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/otp', element: <OtpPage /> },

  // Public tracking — no auth required, mirrors cargo-server's /t/<tn>
  // smart-link landing.
  { path: '/track', element: <PublicTrackPage /> },
  { path: '/track/:trackingNumber', element: <PublicTrackPage /> },
  { path: '/t/:trackingNumber', element: <PublicTrackPage /> },

  {
    path: '/mode-selector',
    element: (
      <ProtectedRoute requireMode={false}>
        <ModeSelectorPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/home',
    element: (
      <ProtectedRoute>
        <HomeRouter />
      </ProtectedRoute>
    ),
  },
  {
    path: '/shipments',
    element: (
      <ProtectedRoute>
        <MyShipmentsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/shipments/create',
    element: (
      <ProtectedRoute>
        <CreateShipmentPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/shipments/:id',
    element: (
      <ProtectedRoute>
        <ShipmentDetailsPage />
      </ProtectedRoute>
    ),
  },

  // ── Driver mode ──────────────────────────────────────────────────
  {
    path: '/trips',
    element: (
      <ProtectedRoute>
        <MyTripsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/trips/create',
    element: (
      <ProtectedRoute>
        <CreateTripPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/driver/jobs',
    element: (
      <ProtectedRoute>
        <DriverShipmentsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/driver/lookup',
    element: (
      <ProtectedRoute>
        <ShortcodeLookupPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/vehicles',
    element: (
      <ProtectedRoute>
        <MyVehiclesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/vehicles/add',
    element: (
      <ProtectedRoute>
        <AddVehiclePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/earnings',
    element: (
      <ProtectedRoute>
        <EarningsPage />
      </ProtectedRoute>
    ),
  },

  // Driver delivery-receipt flows. Wired off /shipments/:id rather than a
  // separate /driver/* path so deep-links from notifications point to the
  // shipment first, then the driver opens the right CTA from the detail.
  {
    path: '/shipments/:id/pickup',
    element: <ProtectedRoute><ConfirmPickupPage /></ProtectedRoute>,
  },
  {
    path: '/shipments/:id/deliver',
    element: <ProtectedRoute><ConfirmDeliveryPage /></ProtectedRoute>,
  },

  // ── Company mode ─────────────────────────────────────────────────
  {
    path: '/company/register',
    element: <ProtectedRoute><CompanyRegisterPage /></ProtectedRoute>,
  },
  {
    path: '/company/edit',
    element: <ProtectedRoute><CompanyEditPage /></ProtectedRoute>,
  },
  {
    path: '/company/members',
    element: <ProtectedRoute><CompanyMembersPage /></ProtectedRoute>,
  },
  {
    path: '/company/sub-mode',
    element: <ProtectedRoute><CompanySubModePage /></ProtectedRoute>,
  },
  {
    path: '/company/pricing-presets',
    element: <ProtectedRoute><CompanyPricingPresetsPage /></ProtectedRoute>,
  },
  {
    path: '/company/reviews',
    element: <ProtectedRoute><CompanyReviewsPage /></ProtectedRoute>,
  },
  {
    path: '/company/customers',
    element: <ProtectedRoute><CompanyCustomersPage /></ProtectedRoute>,
  },
  {
    path: '/company/customers/:customerUserId',
    element: <ProtectedRoute><CompanyCustomerDetailPage /></ProtectedRoute>,
  },
  {
    path: '/company/account',
    element: <ProtectedRoute><CompanyAccountPage /></ProtectedRoute>,
  },
  {
    path: '/company/kyc-docs',
    element: <ProtectedRoute><CompanyKycDocsPage /></ProtectedRoute>,
  },

  // ── Company-driver (fuel refills + trip-plan map) ────────────────
  {
    path: '/company-driver/refills',
    element: <ProtectedRoute><FuelRefillsPage /></ProtectedRoute>,
  },
  {
    path: '/company-driver/trip-plan',
    element: <ProtectedRoute><TripPlanMapPage /></ProtectedRoute>,
  },

  // ── Containers (international consolidated freight) ──────────────
  {
    path: '/containers/my',
    element: <ProtectedRoute><MyContainersPage /></ProtectedRoute>,
  },
  {
    path: '/containers/create',
    element: <ProtectedRoute><CreateContainerPage /></ProtectedRoute>,
  },
  {
    path: '/containers/browse',
    element: <ProtectedRoute><BrowseContainersPage /></ProtectedRoute>,
  },
  {
    path: '/containers/:id',
    element: <ProtectedRoute><ContainerDetailPage /></ProtectedRoute>,
  },
  {
    path: '/containers/:id/join',
    element: <ProtectedRoute><ContainerJoinPage /></ProtectedRoute>,
  },
  {
    path: '/containers/participant/:pid',
    element: <ProtectedRoute><ParticipantDetailPage /></ProtectedRoute>,
  },
  {
    path: '/origin-warehouses',
    element: <ProtectedRoute><OriginWarehousesPage /></ProtectedRoute>,
  },

  // ── Center delegated-shipment flow (migration 163) ───────────────
  {
    path: '/center/create-for-customer',
    element: <ProtectedRoute><CreateForCustomerPage /></ProtectedRoute>,
  },
  {
    path: '/center/delegated-shipments',
    element: <ProtectedRoute><DelegatedShipmentsPage /></ProtectedRoute>,
  },

  // ── Warehouse operator (origin-warehouse, e.g. Guangzhou) ────────
  {
    path: '/warehouse/inbound',
    element: <ProtectedRoute><WarehouseInboundPage /></ProtectedRoute>,
  },

  // Catch-all → home (auth gate handles redirect to /login if needed)
  { path: '*', element: <Navigate to="/home" replace /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
